import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { storyApi, StoryNode } from '../services/api';

interface FullscreenInteractiveStoryProps {
  isVisible: boolean;
  onClose: () => void;
  storySetting: string;
  storyType: string;
  storyStyle: string;
}

const FullscreenInteractiveStory: React.FC<FullscreenInteractiveStoryProps> = ({
  isVisible,
  onClose,
  storySetting,
  storyType,
  storyStyle
}) => {
  const { t } = useTranslation();
  const [currentStoryNode, setCurrentStoryNode] = useState<StoryNode | null>(null);
  const [storyDirections, setStoryDirections] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storyHistory, setStoryHistory] = useState<StoryNode[]>([]);
  const [isControlModalVisible, setIsControlModalVisible] = useState(false);
  const [localStoryType, setLocalStoryType] = useState(storyType);
  const [localStoryStyle, setLocalStoryStyle] = useState(storyStyle);
  const [availableGenres, setAvailableGenres] = useState<string[]>([]);
  const [availableTones, setAvailableTones] = useState<string[]>([]);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);

  // 生成随机方向
  const generateRandomDirections = useCallback(() => {
    // 从常量中随机选择三个不同的方向
    const allDirections = [
      'development',
      'expansion',
      'confrontation',
      'exploration',
      'revelation',
      'conflict',
      'resolution',
      'twist',
      'climax',
      'denouement'
    ];
    
    const shuffled = [...allDirections].sort(() => 0.5 - Math.random());
    setStoryDirections(shuffled.slice(0, 3));
  }, []);

  // 生成初始故事
  const generateInitialStory = useCallback(async () => {
    if (!storySetting) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await storyApi.generateInteractiveStory(storySetting, localStoryType, localStoryStyle);
      if (response.nodes && response.nodes.length > 0) {
        const firstNode = response.nodes[0];
        setCurrentStoryNode(firstNode);
        setStoryHistory([firstNode]);
        // 使用随机方向，因为StoryResponse没有directions属性
        generateRandomDirections();
      } else {
        setError(t('interactiveStory.error.noStoryGenerated'));
      }
    } catch (err) {
      setError(t('interactiveStory.error.failedToGenerate'));
      console.error('Failed to generate initial story:', err);
    } finally {
      setIsLoading(false);
    }
  }, [storySetting, localStoryType, localStoryStyle, generateRandomDirections, t]);

  // 初始化故事
  useEffect(() => {
    if (isVisible && !currentStoryNode) {
      generateInitialStory();
    }
  }, [isVisible, currentStoryNode, generateInitialStory]);

  // 弹窗打开时动态获取类型和风格
  useEffect(() => {
    if (isControlModalVisible) {
      storyApi.getGenres().then(res => setAvailableGenres(res.genres)).catch(() => setAvailableGenres([]));
      storyApi.getTones().then(res => setAvailableTones(res.tones)).catch(() => setAvailableTones([]));
    }
  }, [isControlModalVisible]);



  // 选择方向并生成下一个故事节点
  const handleDirectionSelect = async (direction: string) => {
    if (!currentStoryNode || isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // 构建当前故事路径作为上下文
      const storyContext = storyHistory.map(node => node.content).join('\n');
      
      const response = await storyApi.generateInteractiveStory(
        storyContext,
        localStoryType,
        localStoryStyle,
        direction,
        currentStoryNode.id // 传递当前节点ID，用于扩展故事
      );
      
      if (response.nodes && response.nodes.length > 0) {
        const newNode = response.nodes[0];
        setCurrentStoryNode(newNode);
        setStoryHistory(prev => [...prev, newNode]);
        // 使用随机方向，因为StoryResponse没有directions属性
        generateRandomDirections();
      } else {
        setError(t('interactiveStory.error.noStoryGenerated'));
      }
    } catch (err) {
      setError(t('interactiveStory.error.failedToGenerate'));
      console.error('Failed to generate next story node:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // 返回上一个故事节点
  const handleGoBack = () => {
    if (storyHistory.length <= 1) return;
    
    const newHistory = [...storyHistory];
    newHistory.pop(); // 移除当前节点
    const previousNode = newHistory[newHistory.length - 1];
    
    setCurrentStoryNode(previousNode);
    setStoryHistory(newHistory);
    generateRandomDirections();
  };

  // 导出故事为Markdown文件
  const exportToMarkdown = () => {
    if (storyHistory.length === 0) return;
    
    // 生成Markdown内容
    let markdownContent = `# ${t('interactiveStory.title')}\n\n`;
    markdownContent += `**${t('story.type')}:** ${localStoryType}\n`;
    markdownContent += `**${t('story.style')}:** ${localStoryStyle}\n\n`;
    markdownContent += `## ${t('interactiveStory.storyNodes')}\n\n`;
    
    // 添加每个故事节点
    storyHistory.forEach((node, index) => {
      markdownContent += `### ${index + 1}. ${node.title || node.label || t('interactiveStory.untitledNode')}\n\n`;
      markdownContent += `${node.content || ''}\n\n`;
    });
    
    // 创建Blob并下载
    const blob = new Blob([markdownContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${t('interactiveStory.title')}-${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, #1a2a6c 0%, #16213e 50%, #3a6186 100%)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid rgba(76, 201, 240, 0.3)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2 style={{
            margin: 0,
            color: '#ffffff',
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            <span role="img" aria-label="book">📖</span> {t('interactiveStory.title')}
          </h2>
          
          {/* Story Progress */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(76, 201, 240, 0.1)',
            border: '1px solid rgba(76, 201, 240, 0.3)',
            borderRadius: '8px',
            padding: '8px 12px',
            color: '#4cc9f0',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            <span>{t('interactiveStory.progress', { count: storyHistory.length })}</span>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => setIsHistoryModalVisible(true)}
            style={{
              background: 'rgba(76, 201, 240, 0.2)',
              border: '1px solid rgba(76, 201, 240, 0.5)',
              borderRadius: '8px',
              padding: '8px 16px',
              color: '#4cc9f0',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(76, 201, 240, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(76, 201, 240, 0.2)';
            }}
            disabled={storyHistory.length === 0}
          >
            <span role="img" aria-label="history">📚</span> {t('interactiveStory.viewHistory')}
          </button>

          <button
            onClick={() => setIsControlModalVisible(true)}
            style={{
              background: 'rgba(76, 201, 240, 0.2)',
              border: '1px solid rgba(76, 201, 240, 0.5)',
              borderRadius: '8px',
              padding: '8px 16px',
              color: '#4cc9f0',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(76, 201, 240, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(76, 201, 240, 0.2)';
            }}
          >
            <span role="img" aria-label="settings">⚙️</span> {t('interactiveStory.controls')}
          </button>
          
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 107, 107, 0.2)',
              border: '1px solid rgba(255, 107, 107, 0.5)',
              borderRadius: '8px',
              padding: '8px 16px',
              color: '#ff6b6b',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 107, 107, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 107, 107, 0.2)';
            }}
          >
            ✕ {t('interactiveStory.close')}
          </button>
        </div>
      </div>

      {/* Controls Modal */}
      {isControlModalVisible && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setIsControlModalVisible(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, rgba(26, 42, 108, 0.95) 0%, rgba(58, 97, 134, 0.95) 100%)',
              border: '1px solid rgba(76, 201, 240, 0.3)',
              borderRadius: '16px',
              padding: '32px 28px',
              maxWidth: '420px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              position: 'relative'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ color: '#4cc9f0', fontWeight: 'bold', fontSize: '18px', marginBottom: '18px', textAlign: 'center' }}>
              ⚙️ {t('interactiveStory.storyControls')}
            </h3>
            <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
              {/* Story Type */}
              <div>
                <label style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', display: 'block', marginBottom: '4px' }}>{t('story.type')}</label>
                <select 
                  value={localStoryType} 
                  onChange={e => setLocalStoryType(e.target.value)} 
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    background: 'rgba(0,0,0,0.3)', 
                    color: 'white', 
                    border: '1px solid rgba(255,255,255,0.2)' 
                  }}
                >
                  <option value="">{t('storyInput.genre.placeholder')}</option>
                  {availableGenres.map(g => (
                    <option key={g} value={g}>{t(`story.types.${g.toLowerCase()}`)}</option>
                  ))}
                </select>
              </div>
              {/* Story Style */}
              <div>
                <label style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', display: 'block', marginBottom: '4px' }}>{t('story.style')}</label>
                <select 
                  value={localStoryStyle} 
                  onChange={e => setLocalStoryStyle(e.target.value)} 
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    background: 'rgba(0,0,0,0.3)', 
                    color: 'white', 
                    border: '1px solid rgba(255,255,255,0.2)' 
                  }}
                >
                  <option value="">{t('storyInput.tone.placeholder')}</option>
                  {availableTones.map(tone => (
                    <option key={tone} value={tone}>{t(`story.styles.${tone.toLowerCase()}`)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  // 重置为初始值
                  setLocalStoryType(storyType);
                  setLocalStoryStyle(storyStyle);
                  setIsControlModalVisible(false);
                }}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 107, 107, 0.5)',
                  borderRadius: '8px',
                  color: '#ff6b6b',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('interactiveStory.cancel')}
              </button>
              <button
                onClick={() => {
                  // 应用更改并重置故事历史
                  setCurrentStoryNode(null);
                  setStoryHistory([]);
                  generateInitialStory();
                  setIsControlModalVisible(false);
                }}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(76, 201, 240, 0.2)',
                  border: '1px solid rgba(76, 201, 240, 0.5)',
                  borderRadius: '8px',
                  color: '#4cc9f0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('interactiveStory.apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story History Modal */}
      {isHistoryModalVisible && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setIsHistoryModalVisible(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, rgba(26, 42, 108, 0.95) 0%, rgba(58, 97, 134, 0.95) 100%)',
              border: '1px solid rgba(76, 201, 240, 0.3)',
              borderRadius: '16px',
              padding: '32px 28px',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              position: 'relative'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ color: '#4cc9f0', fontWeight: 'bold', fontSize: '20px', marginBottom: '24px', textAlign: 'center' }}>
              📚 {t('interactiveStory.storyHistory')}
            </h3>
            
            <div style={{ marginBottom: '24px' }}>
              {storyHistory.map((node, index) => (
                <div 
                  key={node.id} 
                  style={{
                    background: 'rgba(67, 97, 238, 0.1)',
                    border: '1px solid rgba(76, 201, 240, 0.3)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '16px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <h4 style={{ 
                    color: '#ffffff', 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    marginTop: 0,
                    marginBottom: '8px'
                  }}>
                    {index + 1}. {node.title || node.label || t('interactiveStory.untitledNode')}
                  </h4>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.8)', 
                    fontSize: '14px',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {node.content}
                  </p>
                </div>
              ))}
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              gap: '10px'
              }}
            >
              <button
                onClick={() => setIsHistoryModalVisible(false)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 107, 107, 0.5)',
                  borderRadius: '8px',
                  color: '#ff6b6b',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('interactiveStory.close')}
              </button>
              
              <button
                onClick={exportToMarkdown}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(76, 201, 240, 0.2)',
                  border: '1px solid rgba(76, 201, 240, 0.5)',
                  borderRadius: '8px',
                  color: '#4cc9f0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span role="img" aria-label="download">📥</span> {t('interactiveStory.exportMarkdown')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        overflow: 'auto'
      }}>
        {/* Error Message */}
        {error && (
          <div style={{
            background: 'rgba(255, 107, 107, 0.1)',
            border: '1px solid rgba(255, 107, 107, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#ff6b6b',
            maxWidth: '800px',
            width: '100%',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Current Story Card */}
        {currentStoryNode && (
          <div style={{
            background: 'linear-gradient(145deg, rgba(26, 42, 108, 0.95), rgba(67, 97, 238, 0.1))',
            border: '2px solid rgba(76, 201, 240, 0.4)',
            borderRadius: '16px',
            padding: '32px',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
            maxWidth: '800px',
            width: '100%',
            marginBottom: '40px',
            transition: 'all 0.3s ease',
            animation: 'fadeIn 0.5s ease-out'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              color: '#ffffff',
              fontSize: '24px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {currentStoryNode.title || currentStoryNode.label}
            </h3>
            <div style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '18px',
              lineHeight: '1.7',
              textAlign: 'center'
            }}>
              {currentStoryNode.content}
            </div>
          </div>
        )}

        {/* Direction Choices */}
        {!isLoading && currentStoryNode && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            maxWidth: '800px',
            width: '100%'
          }}>
            <h4 style={{
              color: '#4cc9f0',
              fontSize: '18px',
              fontWeight: 'bold',
              textAlign: 'center',
              margin: '0 0 8px 0'
            }}>
              {t('interactiveStory.chooseDirection')}
            </h4>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              justifyContent: 'center'
            }}>
              {storyDirections.map((direction, index) => (
                <button
                  key={`${direction}-${index}`}
                  onClick={() => handleDirectionSelect(direction)}
                  style={{
                    background: 'linear-gradient(45deg, #4361ee, #4cc9f0)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    flex: '1 1 calc(33.333% - 16px)',
                    minWidth: '200px',
                    boxShadow: '0 4px 12px rgba(67, 97, 238, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(45deg, #5a73f5, #5dd9f5)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(45deg, #4361ee, #4cc9f0)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <span style={{ fontSize: '24px' }}>
                    {direction === 'development' && '🌱'}
                    {direction === 'expansion' && '🔍'}
                    {direction === 'confrontation' && '⚔️'}
                    {direction === 'exploration' && '🧭'}
                    {direction === 'revelation' && '💡'}
                    {direction === 'conflict' && '🔥'}
                    {direction === 'resolution' && '🤝'}
                    {direction === 'twist' && '🌀'}
                    {direction === 'climax' && '⚡'}
                    {direction === 'denouement' && '🏁'}
                  </span>
                  {t(`storyDirections.${direction}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading Indicator */}
        {isLoading && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(76, 201, 240, 0.1)',
            border: '1px solid rgba(76, 201, 240, 0.3)',
            borderRadius: '8px',
            padding: '8px 12px',
            color: '#4cc9f0',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid rgba(76, 201, 240, 0.3)',
              borderTop: '2px solid #4cc9f0',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            {t('interactiveStory.generating')}
          </div>
        )}
      </div>

      {/* Footer Controls */}
      <div style={{
        padding: '20px',
        borderTop: '1px solid rgba(76, 201, 240, 0.3)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleGoBack}
            disabled={storyHistory.length <= 1 || isLoading}
            style={{
              background: storyHistory.length <= 1 || isLoading ? 'rgba(255, 255, 255, 0.1)' : 'rgba(76, 201, 240, 0.2)',
              border: '1px solid rgba(76, 201, 240, 0.3)',
              borderRadius: '8px',
              padding: '8px 16px',
              color: storyHistory.length <= 1 || isLoading ? 'rgba(255, 255, 255, 0.4)' : '#4cc9f0',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: storyHistory.length <= 1 || isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!(storyHistory.length <= 1 || isLoading)) {
                e.currentTarget.style.background = 'rgba(76, 201, 240, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!(storyHistory.length <= 1 || isLoading)) {
                e.currentTarget.style.background = 'rgba(76, 201, 240, 0.2)';
              }
            }}
          >
            ← {t('interactiveStory.back')}
          </button>
          
          <button
            onClick={exportToMarkdown}
            disabled={storyHistory.length === 0 || isLoading}
            style={{
              background: storyHistory.length === 0 || isLoading ? 'rgba(255, 255, 255, 0.1)' : 'rgba(76, 201, 240, 0.2)',
              border: '1px solid rgba(76, 201, 240, 0.3)',
              borderRadius: '8px',
              padding: '8px 16px',
              color: storyHistory.length === 0 || isLoading ? 'rgba(255, 255, 255, 0.4)' : '#4cc9f0',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: storyHistory.length === 0 || isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              if (!(storyHistory.length === 0 || isLoading)) {
                e.currentTarget.style.background = 'rgba(76, 201, 240, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!(storyHistory.length === 0 || isLoading)) {
                e.currentTarget.style.background = 'rgba(76, 201, 240, 0.2)';
              }
            }}
          >
            <span role="img" aria-label="download">📥</span> {t('interactiveStory.export')}
          </button>
        </div>
        
        <div style={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '14px'
        }}>
          {t('interactiveStory.storyLength', { count: storyHistory.length })}
        </div>
        
        <button
          onClick={onClose}
          style={{
            background: 'rgba(76, 201, 240, 0.2)',
            border: '1px solid rgba(76, 201, 240, 0.3)',
            borderRadius: '8px',
            padding: '8px 16px',
            color: '#4cc9f0',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(76, 201, 240, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(76, 201, 240, 0.2)';
          }}
        >
          {t('interactiveStory.finish')}
        </button>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

export default FullscreenInteractiveStory;